/**
 * Pure helpers shared by the money pages (Invites, Proof review, Payroll,
 * Payouts). Kept free of React so the URL ↔ API mapping and the small
 * decisions that are easy to get wrong (page clamping, which payouts can be
 * retried, who did something) are unit-tested.
 */
import type { PayrollRowState } from './payroll';
import { rowsInScope, DEFAULT_EXCLUDED_TEAMS } from './teams';

// ── Pagination ────────────────────────────────────────────────────────────

/** The last page for `total` rows at `limit` per page. Never below 1. */
export function lastPageFor(total: number, limit: number): number {
  return Math.max(1, Math.ceil(Math.max(0, total) / Math.max(1, limit)));
}

/**
 * Keep a page number inside 1…lastPage, so shrinking a result set (a filter,
 * a row leaving after an action) never strands the view on an empty page.
 */
export function clampPage(page: number, total: number, limit: number): number {
  const p = Number.isFinite(page) ? Math.floor(page) : 1;
  return Math.min(Math.max(1, p), lastPageFor(total, limit));
}

// ── Invites URL → API params ──────────────────────────────────────────────

export const ALL_INVITE_STATUSES = ['PENDING', 'VERIFIED', 'CONVERTED', 'REJECTED'] as const;
/** Every status except Not paid: rejected invites are the long tail admins rarely want. */
export const DEFAULT_INVITE_STATUSES = ['PENDING', 'VERIFIED', 'CONVERTED'] as const;

// ── Invite stages (the Invites page's tabs) ───────────────────────────────

/**
 * The stages GET /admin/invites filters on with `?stage=`, in tab order.
 * Replied = PENDING and not released; Expired = PENDING and released;
 * Joined = VERIFIED; Posted = CONVERTED; Not paid = REJECTED.
 */
export const INVITE_STAGE_FILTERS = ['replied', 'joined', 'posted', 'expired', 'not_paid'] as const;
export type InviteStageFilter = (typeof INVITE_STAGE_FILTERS)[number];

/**
 * Old `?status=` links → stages. PENDING covers both Replied and Expired
 * (the status doesn't change when an invite expires), so a link to "every
 * pending invite" still lands on both.
 */
export const STATUS_STAGES: Record<string, readonly InviteStageFilter[]> = {
  PENDING: ['replied', 'expired'],
  VERIFIED: ['joined'],
  CONVERTED: ['posted'],
  REJECTED: ['not_paid'],
};

function inStageOrder(stages: Iterable<InviteStageFilter>): InviteStageFilter[] {
  const set = new Set(stages);
  return INVITE_STAGE_FILTERS.filter((s) => set.has(s));
}

/** Known stages from a raw `?stage=` list, de-duplicated and in tab order. */
export function parseStages(raw: readonly string[]): InviteStageFilter[] {
  return inStageOrder(
    raw.filter((s): s is InviteStageFilter => (INVITE_STAGE_FILTERS as readonly string[]).includes(s)),
  );
}

/** Stages for a list of raw invite statuses (unknown codes are dropped). */
export function stagesFromStatuses(statuses: readonly string[]): InviteStageFilter[] {
  return inStageOrder(statuses.flatMap((s) => STATUS_STAGES[s] ?? []));
}

/**
 * The stages the page is showing, from its URL. `?stage=` wins; without it a
 * legacy `?status=` list is mapped; with neither (or nothing usable) it's
 * every stage, i.e. the All tab. Never empty.
 */
export function resolveStageSelection(stage: readonly string[], status: readonly string[]): InviteStageFilter[] {
  const fromStage = parseStages(stage);
  if (fromStage.length > 0) return fromStage;
  const fromStatus = stagesFromStatuses(status);
  if (fromStatus.length > 0) return fromStatus;
  return [...INVITE_STAGE_FILTERS];
}

export function isAllStages(stages: readonly InviteStageFilter[]): boolean {
  return INVITE_STAGE_FILTERS.every((s) => stages.includes(s));
}

/** Which tab a selection lights up: 'all', one stage, or null for a mixed set from a link. */
export function stageTabFor(stages: readonly InviteStageFilter[]): InviteStageFilter | 'all' | null {
  if (isAllStages(stages)) return 'all';
  return stages.length === 1 ? stages[0]! : null;
}

/** The status list an older backend (no `stage` support) needs for the same stages. */
export function statusesForStages(stages: readonly InviteStageFilter[]): string[] {
  return ALL_INVITE_STATUSES.filter((status) => STATUS_STAGES[status]!.some((s) => stages.includes(s)));
}

// ── Team scope ────────────────────────────────────────────────────────────

export interface InviteTeamScope {
  team?: string;
  /** Comma list of team codes, as the API's `excludeTeam` takes it. */
  excludeTeam?: string;
}

/**
 * Team params for the Invites page. A picked team (or a team section's
 * lock) is sent as-is. With no team, the Outreach route leaves out
 * DEFAULT_EXCLUDED_TEAMS (Influencer, Discord), unless an
 * outreacher is picked: that already names whose invites to show, and it may
 * be someone on one of those teams.
 */
export function inviteTeamScope(opts: { team: string; locked: boolean; inviterId?: string }): InviteTeamScope {
  if (opts.team) return { team: opts.team };
  if (opts.locked || opts.inviterId) return {};
  return { excludeTeam: DEFAULT_EXCLUDED_TEAMS.join(',') };
}

function applyScope(out: Record<string, string>, scope: InviteTeamScope): void {
  if (scope.team) out.team = scope.team;
  else if (scope.excludeTeam) out.excludeTeam = scope.excludeTeam;
}

// ── Invites URL → API params ──────────────────────────────────────────────

export interface InviteListFilters {
  search: string;
  stages: readonly InviteStageFilter[];
  channel: string;
  inviterId: string;
  niche: string;
  dateFrom: string;
  dateTo: string;
  /**
   * When the creator first posted / joined (`converted_at` / `verified_at`),
   * as `YYYY-MM-DD` days like `dateFrom`/`dateTo` (which are when the invite
   * was logged). Home's "posted / joined this period" counts link with these,
   * so the list holds the same invites as the count. Optional: most links
   * don't set them.
   */
  convertedFrom?: string;
  convertedTo?: string;
  verifiedFrom?: string;
  verifiedTo?: string;
  owed: boolean;
  source: string;
  /** Creator-size windows, as typed (digits). Empty = no bound. */
  minFollowers?: string;
  maxFollowers?: string;
  minAvgViews?: string;
  maxAvgViews?: string;
  /** List order (see INVITE_SORTS). Empty = newest logged first. */
  sort?: string;
  dir?: string;
}

/** Creator-size bounds, in the order the API and the chips list them. */
export const SIZE_FILTERS = ['minFollowers', 'maxFollowers', 'minAvgViews', 'maxAvgViews'] as const;

/**
 * What the Invites list can be ordered by. `column` is the table column that
 * shows the value, so picking a sort can reveal a hidden column. Invites with
 * no value for the chosen column always sort last.
 */
export const INVITE_SORTS = [
  { key: 'logged', label: 'Logged', column: 'date', highFirst: 'Newest logged', lowFirst: 'Oldest logged' },
  { key: 'followers', label: 'Followers', column: 'followers', highFirst: 'Most followers', lowFirst: 'Fewest followers' },
  { key: 'avgViews', label: 'Avg views', column: 'avgViews', highFirst: 'Highest avg views', lowFirst: 'Lowest avg views' },
  { key: 'views', label: 'Clip views', column: 'views', highFirst: 'Most clip views', lowFirst: 'Fewest clip views' },
  { key: 'posts', label: 'Posts', column: 'posts', highFirst: 'Most posts', lowFirst: 'Fewest posts' },
  { key: 'owed', label: 'Owed', column: 'owed', highFirst: 'Most owed', lowFirst: 'Least owed' },
  { key: 'earned', label: 'Earned', column: null, highFirst: 'Highest earned', lowFirst: 'Lowest earned' },
  { key: 'joined', label: 'Joined', column: null, highFirst: 'Joined most recently', lowFirst: 'Joined longest ago' },
  { key: 'posted', label: 'Posted', column: null, highFirst: 'Posted most recently', lowFirst: 'Posted longest ago' },
] as const;
export type InviteSortKey = (typeof INVITE_SORTS)[number]['key'];

/** The URL's `sort` / `dir`, cleaned up. Unknown values fall back to newest logged. */
export function resolveSort(sort: string | undefined, dir: string | undefined): { key: InviteSortKey; dir: 'asc' | 'desc'; isDefault: boolean } {
  const found = INVITE_SORTS.find((s) => s.key === sort);
  const key: InviteSortKey = found?.key ?? 'logged';
  const d = dir === 'asc' ? 'asc' : 'desc';
  return { key, dir: d, isDefault: key === 'logged' && d === 'desc' };
}

/** "Most followers", "Oldest logged", … */
export function sortLabel(key: InviteSortKey, dir: 'asc' | 'desc'): string {
  const s = INVITE_SORTS.find((x) => x.key === key)!;
  return dir === 'asc' ? s.lowFirst : s.highFirst;
}

/** A typed bound as whole digits, or '' when it isn't a usable number. */
export function cleanBound(v: string | undefined): string {
  const digits = (v ?? '').replace(/[^\d]/g, '');
  return digits ? String(Math.min(Number(digits), 2_000_000_000)) : '';
}

/**
 * Quick "logged in the last …" windows for the Invites page. Rolling, so the
 * URL keeps `since=24h` and the start is worked out when the window is picked
 * (a calendar day filter can't say "the last 24 hours").
 */
export const INVITE_SINCE_OPTIONS = [
  { value: '24h', label: '24h', hours: 24 },
  { value: '3d', label: '3 days', hours: 72 },
  { value: '7d', label: '7 days', hours: 168 },
  { value: '14d', label: '14 days', hours: 336 },
  { value: '30d', label: '30 days', hours: 720 },
] as const;

/** The ISO start of a quick window, or '' for none / an unknown value. */
export function sinceStart(since: string, anchorMs: number): string {
  const opt = INVITE_SINCE_OPTIONS.find((o) => o.value === since);
  return opt ? new Date(anchorMs - opt.hours * 3_600_000).toISOString() : '';
}

/** Chip text for a quick window ("last 24h", "last 7 days"). */
export function sinceLabel(since: string): string {
  const opt = INVITE_SINCE_OPTIONS.find((o) => o.value === since);
  return opt ? `last ${opt.label}` : '';
}

/** The posted/joined date filters, in the order the API and the chips list them. */
export const EVENT_DATE_FILTERS = ['convertedFrom', 'convertedTo', 'verifiedFrom', 'verifiedTo'] as const;

/** The filters every invites request shares: the list, the export and the stage counts. */
function sharedInviteParams(
  f: Omit<InviteListFilters, 'stages' | 'owed'>,
  scope: InviteTeamScope,
  opts: { dates?: boolean } = {},
): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.channel) out.channel = f.channel;
  if (f.inviterId) out.inviterId = f.inviterId;
  if (f.niche) out.niche = f.niche;
  applyScope(out, scope);
  const search = f.search.trim();
  if (search) out.search = search;
  if (opts.dates !== false) {
    if (f.dateFrom) out.dateFrom = f.dateFrom;
    if (f.dateTo) out.dateTo = f.dateTo;
  }
  // Saved views only replace the logged-at range, so these always apply.
  for (const key of EVENT_DATE_FILTERS) {
    const v = f[key];
    if (v) out[key] = v;
  }
  if (f.source) out.discoveryFolder = f.source;
  for (const key of SIZE_FILTERS) {
    const v = cleanBound(f[key]);
    if (v) out[key] = v;
  }
  return out;
}

/**
 * Backend params for GET /admin/invites (and the CSV export) from the page's
 * URL filters. `stage` is always sent, All included, so All means the same
 * thing as its count. `status` rides along for a backend that predates
 * `stage` (which takes precedence when both arrive); All omits it, which
 * an older backend reads as every status.
 */
export function invitesQueryParams(f: InviteListFilters, scope: InviteTeamScope): Record<string, string> {
  const stages = f.stages.length > 0 ? inStageOrder(f.stages) : [...INVITE_STAGE_FILTERS];
  const out: Record<string, string> = { stage: stages.join(',') };
  if (!isAllStages(stages)) out.status = statusesForStages(stages).join(',');
  Object.assign(out, sharedInviteParams(f, scope));
  if (f.owed) out.showOwed = 'true';
  // The default order is the server's own, so it isn't sent: an older backend never sees the param.
  const order = resolveSort(f.sort, f.dir);
  if (!order.isDefault) {
    out.sort = order.key;
    out.dir = order.dir;
  }
  return out;
}

/**
 * Params for GET /admin/invites/stage-counts: every filter except the stage
 * itself, so each tab's count matches the list it opens (Owed only included).
 * `dates: false` drops the logged-at range, for the saved-view tiles that
 * set their own.
 */
export function stageCountsQueryParams(
  f: Omit<InviteListFilters, 'stages' | 'owed'> & { owed?: boolean },
  scope: InviteTeamScope,
  opts: { dates?: boolean } = {},
): Record<string, string> {
  const out = sharedInviteParams(f, scope, opts);
  if (f.owed) out.showOwed = 'true';
  return out;
}

/** The "Messaged · no reply" list only understands channel, team scope and search. */
export function contactedQueryParams(
  f: Pick<InviteListFilters, 'search' | 'channel'>,
  scope: InviteTeamScope,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.channel) out.channel = f.channel;
  applyScope(out, scope);
  const search = f.search.trim();
  if (search) out.search = search;
  return out;
}

// ── Saved views ───────────────────────────────────────────────────────────

export type SavedInviteView = 'waiting' | 'joinedNoPost' | 'loggedThisWeek';
export const SAVED_INVITE_VIEWS: readonly SavedInviteView[] = ['waiting', 'joinedNoPost', 'loggedThisWeek'];

/** `days` before `now` as a local YYYY-MM-DD, the format the date filters use. */
export function daysAgoYmd(now: Date, days: number): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface SavedViewPatch {
  /** `[]` is the All tab. */
  stage: string[];
  status: string[];
  dateFrom: string;
  dateTo: string;
}

/** The URL patch a saved view applies. It replaces the stage and the date range. */
export function savedViewPatch(view: SavedInviteView, now: Date): SavedViewPatch {
  const weekAgo = daysAgoYmd(now, 7);
  switch (view) {
    case 'waiting':
      return { stage: ['replied'], status: [], dateFrom: '', dateTo: weekAgo };
    case 'joinedNoPost':
      return { stage: ['joined'], status: [], dateFrom: '', dateTo: '' };
    case 'loggedThisWeek':
      return { stage: [], status: [], dateFrom: weekAgo, dateTo: '' };
  }
}

/** The patch that turns a saved view back off: All, no date range. */
export const CLEAR_SAVED_VIEW: SavedViewPatch = { stage: [], status: [], dateFrom: '', dateTo: '' };

/** The saved view the current filters match, if any (so its tile reads as active). */
export function activeSavedView(
  f: { stages: readonly InviteStageFilter[]; dateFrom: string; dateTo: string },
  now: Date,
): SavedInviteView | null {
  for (const view of SAVED_INVITE_VIEWS) {
    const p = savedViewPatch(view, now);
    const stages = resolveStageSelection(p.stage, []);
    const sameStages = stages.length === f.stages.length && stages.every((s) => f.stages.includes(s));
    if (sameStages && p.dateFrom === f.dateFrom && p.dateTo === f.dateTo) return view;
  }
  return null;
}

// ── Stage age ─────────────────────────────────────────────────────────────

interface StageAgeInput {
  createdAt: string;
  convertedAt?: string | null;
  releasedAt?: string | null;
  deletedAt?: string | null;
  /** When the creator joined. Null on invites that joined before it was recorded. */
  verifiedAt?: string | null;
}

/**
 * When an invite entered the stage it's in, if we know. Replied counts from
 * when it was logged; Joined from `verifiedAt`; Posted, Expired and Hidden
 * from their own timestamps. Not paid has no rejection time on the row, so
 * it (and a Joined invite without `verifiedAt`) has no age rather than a
 * guessed one.
 */
export function stageSince(stage: string, inv: StageAgeInput): string | null {
  switch (stage) {
    case 'replied':
    case 'manual':
      return inv.createdAt;
    case 'joined':
      return inv.verifiedAt ?? null;
    case 'posted':
      return inv.convertedAt ?? null;
    case 'expired':
      return inv.releasedAt ?? null;
    case 'hidden':
      return inv.deletedAt ?? null;
    default:
      return null;
  }
}

/** Whole days from `since` to `now`; null when `since` is missing or unreadable. */
export function ageInDays(since: string | null | undefined, now: Date): number | null {
  if (!since) return null;
  const t = new Date(since).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
}

/** "today", "9d", "4mo". */
export function formatAgeDays(days: number): string {
  if (days < 1) return 'today';
  if (days < 90) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

/** The short age shown next to a stage ("9d"), or null when it isn't known. */
export function stageAgeLabel(stage: string, inv: StageAgeInput, now: Date): string | null {
  const days = ageInDays(stageSince(stage, inv), now);
  return days === null ? null : formatAgeDays(days);
}

/**
 * A link that lands on one invite in the admin Invites page: searched by
 * handle, every status included (so a Not paid invite isn't filtered out),
 * and `open` set so the detail sheet opens when the row is on the page.
 */
export function inviteLink(opts: {
  handle: string;
  openPublicId?: string | null;
  inviterId?: number | string | null;
  base?: string;
}): string {
  const params = new URLSearchParams();
  params.set('search', opts.handle.replace(/^@+/, ''));
  params.set('status', ALL_INVITE_STATUSES.join(','));
  if (opts.inviterId !== undefined && opts.inviterId !== null && opts.inviterId !== '') {
    params.set('inviterId', String(opts.inviterId));
  }
  if (opts.openPublicId) params.set('open', opts.openPublicId);
  return `${opts.base ?? '/admin/invites'}?${params.toString()}`;
}

/**
 * Every invite one outreacher logged, in every status. Without `status` the
 * Invites page applies its default filter, which hides Not paid invites.
 */
export function inviterInvitesLink(inviterId: number | string, base = '/admin/invites'): string {
  const params = new URLSearchParams();
  params.set('inviterId', String(inviterId));
  params.set('status', ALL_INVITE_STATUSES.join(','));
  return `${base}?${params.toString()}`;
}

// ── Payroll ───────────────────────────────────────────────────────────────

export const PAYROLL_STATES: readonly PayrollRowState[] = ['ready', 'blocked', 'in_flight', 'idle'];

/** `?state=` from the URL, or '' when absent or not a known state. */
export function parsePayrollState(raw: string | null | undefined): PayrollRowState | '' {
  return (PAYROLL_STATES as readonly string[]).includes(raw ?? '') ? (raw as PayrollRowState) : '';
}

/**
 * Payroll rows for a team scope (the payroll endpoint returns every team).
 * `team` keeps that team; otherwise `excludeTeams` are left out.
 */
export function rowsForTeam<T extends { team: string }>(
  rows: readonly T[],
  team: string,
  excludeTeams: readonly string[] = [],
): T[] {
  return rowsInScope(rows, { team, excludeTeams });
}

/** Payment detail fields the payout route can be missing, in words. */
export const MISSING_FIELD_LABELS: Record<string, string> = {
  clippingId: 'Clipping account',
  paypalEmail: 'PayPal email',
  email: 'Email',
  inviterPublicId: 'Outreacher ID',
  name: 'Name',
};
export function missingFieldLabel(field: string): string {
  return MISSING_FIELD_LABELS[field] ?? 'Payment detail';
}

// ── Payouts ───────────────────────────────────────────────────────────────

export const PAYOUT_STATUSES = ['PENDING', 'SUBMITTED', 'PAID', 'FAILED', 'CANCELLED'] as const;
export const PAYOUT_PROVIDERS = ['clipping', 'financial', 'local'] as const;

/**
 * Retry send re-fires the provider call. Only worth offering when the send
 * failed or the provider hasn't confirmed it yet; a Queued payout is still
 * on its way and a Paid / Cancelled one is finished.
 */
export function canRetryPayout(status: string): boolean {
  return status === 'FAILED' || status === 'SUBMITTED';
}

/** Payouts that haven't settled can still be cancelled (or voided, when failed). */
export function canCancelPayout(status: string): boolean {
  return status === 'PENDING' || status === 'SUBMITTED' || status === 'FAILED';
}

/** Backend params for GET /admin/payouts from the page's URL filters. */
export function payoutsQueryParams(f: {
  status: string;
  inviter: string;
  provider: string;
  page: number;
  limit: number;
}): Record<string, string | number> {
  const out: Record<string, string | number> = { page: Math.max(1, f.page), limit: Math.min(200, f.limit) };
  if ((PAYOUT_STATUSES as readonly string[]).includes(f.status)) out.status = f.status;
  if (/^\d+$/.test(f.inviter)) out.inviterId = f.inviter;
  if ((PAYOUT_PROVIDERS as readonly string[]).includes(f.provider)) out.providerKey = f.provider;
  return out;
}

// ── Payment history ───────────────────────────────────────────────────────

export const PAYMENT_SOURCE_LABELS: Record<string, string> = {
  PAYOUT: 'Payout',
  RECONCILE: 'Provider sync',
  ADJUSTMENT: 'Adjustment',
  REJECT: 'Rejection write-off',
  SETTLEMENT: 'Settled',
  IMPORT: 'Imported',
  REVERSAL: 'Undo',
};
export function paymentSourceLabel(source: string): string {
  return PAYMENT_SOURCE_LABELS[source] ?? 'Other';
}

/**
 * Who did something, from a ledger `createdBy` / `screenshotReviewedBy`.
 * The backend stores either "system:…" for automated writes or the admin's
 * sign-in id, never a name, so the only name we can show is the viewer's own.
 */
export function actorLabel(
  by: string | null | undefined,
  me?: { id?: string | null; name?: string | null },
): string {
  const v = (by ?? '').trim();
  if (!v || v.startsWith('system')) return 'System';
  if (me?.id && v === me.id) return me.name?.trim() || 'You';
  // Sign-in ids look like "user_2abc…": a code, not a name.
  if (/^user_[A-Za-z0-9]+$/.test(v)) return 'An admin';
  return v;
}

// ── Proof review ──────────────────────────────────────────────────────────

/** The queue minus anything already reviewed in this session. */
export function withoutReviewed<T extends { invitePublicId: string }>(
  items: readonly T[],
  reviewed: ReadonlySet<string>,
): T[] {
  return reviewed.size === 0 ? [...items] : items.filter((i) => !reviewed.has(i.invitePublicId));
}

/** Keep a cursor inside a list of `length` items (0 when empty). */
export function clampCursor(cursor: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(0, cursor), length - 1);
}
